import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export type CredentialsNextLinks = {
  atelierPath?: string;
  subscriptionPath?: string;
  paymentsPath?: string;
};

interface CredentialsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  password: string;
  tenantName?: string;
  loginUrl?: string;
  nextLinks?: CredentialsNextLinks;
}

export default function CredentialsPopup({
  isOpen,
  onClose,
  email,
  password,
  tenantName,
  loginUrl,
  nextLinks,
}: CredentialsPopupProps) {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState<'email' | 'password' | 'loginUrl' | null>(null);

  useEffect(() => {
    if (copiedField) {
      const timer = setTimeout(() => setCopiedField(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedField]);

  if (!isOpen) return null;

  const handleCopy = async (text: string, field: 'email' | 'password' | 'loginUrl') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyAllLines = [
    `${t('credentials.popup.email')}: ${email}`,
    `${t('credentials.popup.password')}: ${password}`,
    loginUrl ? `${t('credentials.popup.login_url')}: ${loginUrl}` : '',
  ].filter(Boolean);

  const hasNext =
    Boolean(nextLinks?.atelierPath) ||
    Boolean(nextLinks?.subscriptionPath) ||
    Boolean(nextLinks?.paymentsPath);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      style={{ animation: 'fadeIn 200ms ease-out' }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
        style={{ animation: 'slideUp 300ms ease-out' }}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <i className="ri-check-line text-emerald-600 text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{t('credentials.popup.title')}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{t('credentials.popup.subtitle')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {tenantName ? (
            <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
              <p className="text-xs text-blue-600 font-medium mb-1">{t('credentials.popup.tenant')}</p>
              <p className="text-sm font-mono text-blue-900">{tenantName}</p>
            </div>
          ) : null}

          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {t('credentials.popup.email')}
            </label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <i className="ri-mail-line text-gray-400 text-lg" />
              <code className="flex-1 text-sm font-mono text-gray-800 break-all">{email}</code>
              <button
                type="button"
                onClick={() => void handleCopy(email, 'email')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-teal-600 hover:bg-teal-50 transition-all"
                title={t('credentials.popup.copy')}
              >
                {copiedField === 'email' ? (
                  <i className="ri-check-line text-emerald-500 text-base" />
                ) : (
                  <i className="ri-file-copy-line text-base" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {t('credentials.popup.password')}
            </label>
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
              <i className="ri-key-2-line text-amber-600 text-lg" />
              <code className="flex-1 text-sm font-mono text-gray-800 break-all">{password}</code>
              <button
                type="button"
                onClick={() => void handleCopy(password, 'password')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-100 transition-all"
                title={t('credentials.popup.copy')}
              >
                {copiedField === 'password' ? (
                  <i className="ri-check-line text-emerald-500 text-base" />
                ) : (
                  <i className="ri-file-copy-line text-base" />
                )}
              </button>
            </div>
          </div>

          {loginUrl ? (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {t('credentials.popup.login_url')}
              </label>
              <div className="flex items-center gap-2 p-3 bg-teal-50 rounded-xl border border-teal-100">
                <i className="ri-link text-teal-600 text-lg" />
                <a
                  href={loginUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-sm font-mono text-teal-700 hover:underline break-all"
                >
                  {loginUrl}
                </a>
                <button
                  type="button"
                  onClick={() => void handleCopy(loginUrl, 'loginUrl')}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-teal-600 hover:bg-teal-50 transition-all"
                  title={t('credentials.popup.copy')}
                >
                  {copiedField === 'loginUrl' ? (
                    <i className="ri-check-line text-emerald-500 text-base" />
                  ) : (
                    <i className="ri-file-copy-line text-base" />
                  )}
                </button>
              </div>
            </div>
          ) : null}

          {hasNext ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {t('credentials.popup.next_steps')}
              </p>
              <div className="flex flex-wrap gap-2">
                {nextLinks?.atelierPath ? (
                  <Link
                    to={nextLinks.atelierPath}
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-xs font-medium text-gray-700 hover:bg-gray-200"
                  >
                    <i className="ri-store-2-line" />
                    {t('credentials.popup.open_atelier')}
                  </Link>
                ) : null}
                {nextLinks?.subscriptionPath ? (
                  <Link
                    to={nextLinks.subscriptionPath}
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-xs font-medium text-gray-700 hover:bg-gray-200"
                  >
                    <i className="ri-checkbox-circle-line" />
                    {t('credentials.popup.open_subscription')}
                  </Link>
                ) : null}
                {nextLinks?.paymentsPath ? (
                  <Link
                    to={nextLinks.paymentsPath}
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-xs font-medium text-gray-700 hover:bg-gray-200"
                  >
                    <i className="ri-bank-card-line" />
                    {t('credentials.popup.open_payments')}
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-xl border border-red-100">
            <i className="ri-alert-line text-red-500 text-sm mt-0.5" />
            <p className="text-xs text-red-700">{t('credentials.popup.warning')}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-5 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            {t('credentials.popup.close')}
          </button>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(copyAllLines.join('\n'));
              setCopiedField(null);
            }}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-teal-600 rounded-xl hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
          >
            <i className="ri-clipboard-line text-base" />
            {t('credentials.popup.copy_all')}
          </button>
        </div>
      </div>
    </div>
  );
}
